import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import useAuthStore from '../store/authStore';
import useUiStore from '../store/uiStore';
import useBrandingStore from '../store/brandingStore';
import useTenantStore from '../store/tenantStore';
import { LogoMark } from './Logo';
import { getWsOrigin, messagingService, enrollmentService, feedService } from '../services/api';
import { showLocalNotification } from '../utils/localNotifications';
import SocialLinks from './SocialLinks';

// Every route currently mounts its own Sidebar. Keep the unread badge cache at
// module scope so navigating between pages does not trigger another request.
let unreadCache = { value: 0, fetchedAt: 0, promise: null };
const UNREAD_CACHE_MS = 25000;

async function loadUnreadCount(force = false) {
  const now = Date.now();
  if (!force && now - unreadCache.fetchedAt < UNREAD_CACHE_MS) return unreadCache.value;
  if (unreadCache.promise) return unreadCache.promise;
  unreadCache.promise = messagingService.getUnreadCount()
    .then((response) => {
      const value = Number(response.data?.unread_count || 0);
      unreadCache = { value, fetchedAt: Date.now(), promise: null };
      return value;
    })
    .catch((error) => {
      unreadCache.promise = null;
      throw error;
    });
  return unreadCache.promise;
}

// Same module-scope cache pattern as the messages badge above, for every
// other nav-item badge (announcements, pending enrollments, comments &
// questions). One combined fetch, shared TTL, polled on the same interval
// the messages badge already uses below - never a separate timer per badge,
// so adding more badges never adds more background requests.
const EMPTY_BADGES = { announcements: 0, pending_enrollments: 0, comments: 0 };
let badgeCache = { value: EMPTY_BADGES, fetchedAt: 0, promise: null };

async function loadBadgeCounts(isTeacher, force = false) {
  const now = Date.now();
  if (!force && now - badgeCache.fetchedAt < UNREAD_CACHE_MS) return badgeCache.value;
  if (badgeCache.promise) return badgeCache.promise;
  badgeCache.promise = Promise.all([
    enrollmentService.getBadgeSummary(),
    isTeacher ? feedService.getTeacherEngagementUnreadCount() : Promise.resolve(null),
  ])
    .then(([summaryRes, engagementRes]) => {
      const value = {
        announcements: Number(summaryRes.data?.announcements || 0),
        pending_enrollments: Number(summaryRes.data?.pending_enrollments || 0),
        comments: Number(engagementRes?.data?.unread_count || 0),
      };
      badgeCache = { value, fetchedAt: Date.now(), promise: null };
      return value;
    })
    .catch((error) => {
      badgeCache.promise = null;
      throw error;
    });
  return badgeCache.promise;
}

// Turns a live `user.notification` WebSocket payload into a title/body pair
// for a local notification banner. Mirrors the two shapes the backend sends:
// chat messages (source: 'group'|'private') and generic app events
// (source: 'notification', from enrollments/notify.py).
function describeNotification(payload) {
  if (payload.source === 'notification') {
    const n = payload.notification || {};
    return { title: "L'mcid", body: n.verb, targetUrl: n.target_url };
  }
  if (payload.source === 'group' || payload.source === 'private') {
    const msg = payload.payload || {};
    const sender = msg.sender ? `${msg.sender.first_name} ${msg.sender.last_name}` : "L'mcid";
    return { title: sender, body: msg.content, targetUrl: '/messages' };
  }
  return null;
}

// A small count pill for "something new here" nav badges. Uses the same
// accent color as the active-link pill and the collapse toggle, instead of
// alarm-red, so an unread badge reads as "notable" rather than "urgent/
// something's wrong" - and a thin ring in the sidebar's own background color
// cuts it out cleanly from whatever it's sitting on top of.
function NavBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-2 rtl:ml-0 rtl:mr-2 inline-flex items-center justify-center bg-accent-500 text-white text-[11px] font-semibold min-w-[1.25rem] h-5 px-1 rounded-full shrink-0 ring-2 ring-sidebar">
      {count > 99 ? '99+' : count}
    </span>
  );
}

// One nav row, active/hover/collapsed states handled in a single place
// instead of ~20 near-identical inline className strings. The active state
// is a solid accent-colored pill (not a translucent overlay) so it reads
// unmistakably against the deep indigo ground, with the icon riding on its
// own chip for a two-tier "selected" look.
function SidebarLink({ to, icon, label, active, open, badge }) {
  const [tooltipPosition, setTooltipPosition] = useState(null);

  const showTooltip = (event) => {
    if (open) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl';
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      ...(rtl
        ? { right: window.innerWidth - rect.left + 10 }
        : { left: rect.right + 10 }),
    });
  };

  const hideTooltip = () => setTooltipPosition(null);

  return (
    <>
      <Link
        to={to}
        aria-label={!open ? label : undefined}
        aria-current={active ? 'page' : undefined}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
          active
            ? 'bg-accent-500 text-white shadow-md shadow-black/20'
            : 'text-primary-200 hover:bg-white/8 hover:text-white'
        } ${!open ? 'justify-center' : ''}`}
      >
        <span className={`w-5 h-5 shrink-0 ${active ? '' : 'opacity-80'}`}>{icon}</span>
        {open && (
          <span className="flex items-center justify-between flex-1 min-w-0">
            <span className={`truncate ${active ? 'font-semibold' : ''}`}>{label}</span>
            {badge}
          </span>
        )}
      </Link>
      {!open && tooltipPosition && createPortal(
        <span
          role="tooltip"
          style={tooltipPosition}
          className="pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-950 px-3 py-2 text-xs font-semibold text-white shadow-xl"
        >
          {label}
        </span>,
        document.body,
      )}
    </>
  );
}

function Sidebar() {
  const navRef = useRef(null);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const { user } = useAuthStore();
  const mobileOpen = useUiStore((s) => s.mobileSidebarOpen);
  const closeMobileSidebar = useUiStore((s) => s.closeMobileSidebar);
  const location = useLocation();
  const { siteName, logo: brandLogo, socialLinks, load: loadBranding } = useBrandingStore();

  useEffect(() => {
    closeMobileSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => { loadBranding(); }, [loadBranding]);

  const isTeacher = user?.roles?.some(r => r.name.toLowerCase() === 'teacher');
  const isStudent = user?.roles?.some(r => r.name.toLowerCase() === 'student');
  const isPlatformOwner = Boolean(user?.is_platform_owner);
  const isAdministrator = isPlatformOwner || user?.roles?.some(r => r.name.toLowerCase() === 'administrator');
  const features = useTenantStore((state) => state.features);
  const enabled = (feature) => features[feature] !== false;
  const isParent = user?.roles?.some(r => r.name.toLowerCase() === 'parent');
  const { t } = useTranslation();

  const isActive = (path) => location.pathname === path;

  // A refreshed admin page starts with the long navigation list scrolled to
  // the top. Keep the current route visible without moving the list when the
  // selected item is already within its viewport.
  useLayoutEffect(() => {
    const nav = navRef.current;
    const activeLink = nav?.querySelector('[aria-current="page"]');
    if (!nav || !activeLink) return;

    const linkTop = activeLink.offsetTop;
    const linkBottom = linkTop + activeLink.offsetHeight;
    const visibleTop = nav.scrollTop;
    const visibleBottom = visibleTop + nav.clientHeight;

    if (linkTop < visibleTop || linkBottom > visibleBottom) {
      nav.scrollTop = Math.max(0, linkTop - (nav.clientHeight - activeLink.offsetHeight) / 2);
    }
  }, [location.pathname, sidebarOpen, user?.id, isAdministrator, isParent, isStudent, isTeacher]);

  const [unreadCount, setUnreadCount] = useState(0);
  const [badgeCounts, setBadgeCounts] = useState(EMPTY_BADGES);

  useEffect(() => {
    let mounted = true;
    const fetchUnread = async (force = false) => {
      if (!enabled('messaging')) {
        if (mounted) setUnreadCount(0);
        return;
      }
      try {
        const total = await loadUnreadCount(force);
        if (mounted) setUnreadCount(total);
      } catch (e) {
        // swallow errors; badge is non-critical
      }
    };
    const fetchBadges = async (force = false) => {
      try {
        const counts = await loadBadgeCounts(isTeacher && enabled('community_feed'), force);
        if (mounted) setBadgeCounts(counts);
      } catch (e) {
        // swallow errors; badges are non-critical
      }
    };
    fetchUnread();
    fetchBadges();
    // Same 30s cadence as the messages badge - one shared timer for every
    // badge on the sidebar, not one per badge.
    const id = setInterval(() => { fetchUnread(true); fetchBadges(true); }, 30000);
    // listen for immediate updates when conversations state changes elsewhere
    const handler = (e) => {
      try {
        const c = e.detail?.unreadCount;
        if (typeof c === 'number') {
          unreadCache = { value: c, fetchedAt: Date.now(), promise: null };
          setUnreadCount(c);
        }
      } catch (err) {}
    };
    window.addEventListener('conversations.unread_updated', handler);
    // Also open a user-level websocket for instant notifications
    let ws;
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${getWsOrigin()}/ws/messaging/user/?token=${token}`;
      ws = new WebSocket(url);
      ws.onopen = () => console.log('Sidebar user WS connected');
      ws.onmessage = async (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'notification') {
            // Refresh conversations counts
            await fetchUnread(true);
            await fetchBadges(true);

            const described = describeNotification(data.payload || {});
            if (described) {
              showLocalNotification(described.title, described.body, { target_url: described.targetUrl });
            }
          }
        } catch (e) { console.error('Sidebar WS parse error', e); }
      };
      ws.onclose = () => console.log('Sidebar user WS closed');
      ws.onerror = (e) => console.error('Sidebar WS error', e);
    } catch (e) {}
    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener('conversations.unread_updated', handler);
      if (ws && ws.readyState < WebSocket.CLOSING) ws.close();
    };
    // Reconnect only when an entitlement that affects the polled channels
    // changes (normally once, after tenant resolution).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.messaging, features.community_feed, isTeacher]);

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}
      <aside
        className={`app-sidebar fixed md:static inset-y-0 left-0 rtl:left-auto rtl:right-0 z-50 md:z-20
          ${mobileOpen ? 'translate-x-0 rtl:translate-x-0' : '-translate-x-full rtl:translate-x-full'} md:!translate-x-0
          ${sidebarOpen ? 'w-64' : 'w-20'}
          md:relative bg-sidebar text-white transition-transform md:transition-all duration-300 flex flex-col
          pt-[env(safe-area-inset-top)]`}
      >
      {/* Faint brand dot-grid, same texture as the dashboard hero banner,
          so the shell and the content read as one system. */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />

      {/* Floating collapse toggle straddling the sidebar's edge — desktop only. */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="hidden md:flex absolute top-6 -right-3 rtl:right-auto rtl:-left-3 z-10 w-6 h-6 items-center justify-center rounded-full bg-accent-500 text-white shadow-md shadow-black/30 hover:bg-accent-600 transition"
        title={sidebarOpen ? t('Collapse sidebar') : t('Expand sidebar')}
      >
        <svg className={`w-3.5 h-3.5 transition-transform rtl:rotate-180 ${sidebarOpen ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div className="relative p-4 border-b border-white/10">
        {sidebarOpen ? (
          <div className="flex items-center justify-between">
            {Capacitor.isNativePlatform() ? (
              <div className="flex items-center gap-2">
                {brandLogo ? <img src={brandLogo} alt="" className="w-8 h-8 object-contain shrink-0" /> : <LogoMark size={32} />}
                <h1 className="text-xl font-bold font-display tracking-tight">{siteName || t('AppName')}</h1>
              </div>
            ) : (
              <Link to="/" className="flex items-center gap-2" title={t('Visit website')}>
                {brandLogo ? <img src={brandLogo} alt="" className="w-8 h-8 object-contain shrink-0" /> : <LogoMark size={32} />}
                <h1 className="text-xl font-bold font-display tracking-tight">{siteName || t('AppName')}</h1>
              </Link>
            )}
            <button onClick={closeMobileSidebar} className="md:hidden p-2 hover:bg-white/10 rounded-lg transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="hidden md:flex flex-col items-center">
            {Capacitor.isNativePlatform() ? (
              brandLogo ? <img src={brandLogo} alt="" className="w-8 h-8 object-contain shrink-0" /> : <LogoMark size={32} />
            ) : (
              <Link to="/" title={t('Visit website')}>
                {brandLogo ? <img src={brandLogo} alt="" className="w-8 h-8 object-contain shrink-0" /> : <LogoMark size={32} />}
              </Link>
            )}
          </div>
        )}
      </div>

      <nav ref={navRef} className="relative flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <SidebarLink
          to="/dashboard"
          open={sidebarOpen}
          active={isActive('/dashboard')}
          label={t('Dashboard')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          }
        />

        {!isAdministrator && !isParent && (
          <SidebarLink
            to="/courses"
            open={sidebarOpen}
            active={isActive('/courses')}
            label={isTeacher ? t('My Modules') : t('My Courses')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
        )}

        {enabled('calendar') && <SidebarLink
          to="/calendar"
          open={sidebarOpen}
          active={isActive('/calendar')}
          label={t('Calendar')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />}

        {enabled('resources') && <SidebarLink
          to="/resources"
          open={sidebarOpen}
          active={isActive('/resources')}
          label={t('Resources')}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          }
        />}

        {isStudent && (
          <>
            <SidebarLink
              to="/store"
              open={sidebarOpen}
              active={isActive('/store')}
              label={t('Store')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 10-8 0v4" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 11h18v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6z" />
                </svg>
              }
            />
            <SidebarLink
              to="/enrollments"
              open={sidebarOpen}
              active={isActive('/enrollments')}
              label={t('My Enrollments')}
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                  <path d="M20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                </svg>
              }
            />
          </>
        )}

        {enabled('announcements') && <SidebarLink
          to="/announcements"
          open={sidebarOpen}
          active={isActive('/announcements')}
          label={t('Announcements')}
          badge={<NavBadge count={badgeCounts.announcements} />}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          }
        />}

        {enabled('messaging') && <SidebarLink
          to="/messages"
          open={sidebarOpen}
          active={isActive('/messages')}
          label={t('Messages')}
          badge={<NavBadge count={unreadCount} />}
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          }
        />}

        {isTeacher && (
          <SidebarLink
            to="/students"
            open={sidebarOpen}
            active={isActive('/students')}
            label={t('Students')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
          />
        )}

        {isTeacher && (
          <SidebarLink
            to="/teacher/enrollments"
            open={sidebarOpen}
            active={isActive('/teacher/enrollments')}
            label={t('Enrollments')}
            badge={<NavBadge count={badgeCounts.pending_enrollments} />}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
        )}

        {isTeacher && enabled('teacher_blogs') && (
          <SidebarLink
            to="/teacher/blog"
            open={sidebarOpen}
            active={isActive('/teacher/blog')}
            label={t('My Blog')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            }
          />
        )}

        {isTeacher && enabled('community_feed') && (
          <SidebarLink
            to="/teacher/comments"
            open={sidebarOpen}
            active={isActive('/teacher/comments')}
            label={t('Comments & Questions')}
            badge={<NavBadge count={badgeCounts.comments} />}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
          />
        )}

        {isTeacher && enabled('resources') && (
          <SidebarLink
            to="/teacher/documents"
            open={sidebarOpen}
            active={isActive('/teacher/documents')}
            label={t('My Documents')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        )}

        {isTeacher && user?.id && enabled('teacher_profiles') && (
          <SidebarLink
            to={`/teachers/${user.id}`}
            open={sidebarOpen}
            active={isActive(`/teachers/${user.id}`)}
            label={t('My Public Profile')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        )}

        {isTeacher && enabled('qr_sharing') && (
          <SidebarLink
            to="/teacher/share"
            open={sidebarOpen}
            active={isActive('/teacher/share')}
            label={t('QR sharing studio')}
            icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h2m4 0v2m-6 4h2m2-2h2m-6-2h6" /></svg>}
          />
        )}

        {isTeacher && enabled('payments') && (
          <SidebarLink
            to="/payment-accounts"
            open={sidebarOpen}
            active={isActive('/payment-accounts')}
            label={t('Payment Accounts')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m12 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2z" />
              </svg>
            }
          />
        )}

        {isTeacher && enabled('payments') && (
          <SidebarLink
            to="/payment-plans"
            open={sidebarOpen}
            active={isActive('/payment-plans')}
            label={t('Payment Plans')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          />
        )}

        {(isParent || isStudent) && enabled('parent_portal') && (
          <SidebarLink
            to="/family-links"
            open={sidebarOpen}
            active={isActive('/family-links') || isActive('/parent/link-child')}
            label={t('Family connections')}
            icon={
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM19 8v4m-2-2h4" />
              </svg>
            }
          />
        )}

        {isAdministrator && (
          <>
            {sidebarOpen ? (
              <div className="flex items-center gap-2 px-3 pt-5 pb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-accent-400">{t('Administration')}</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
            ) : (
              <div className="pt-5 pb-1.5 flex justify-center">
                <span className="w-6 h-px bg-white/15" />
              </div>
            )}
            {isPlatformOwner && (
              <SidebarLink
                to="/platform/schools"
                open={sidebarOpen}
                active={isActive('/platform/schools')}
                label={t('School Control Plane')}
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14M9 10h.01M9 14h.01M9 18h.01M15 10h.01M15 14h.01M15 18h.01" />
                  </svg>
                }
              />
            )}
            <SidebarLink
              to="/admin/users"
              open={sidebarOpen}
              active={isActive('/admin/users')}
              label={t('Users')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4" />
                </svg>
              }
            />
            <SidebarLink
              to="/admin/classes"
              open={sidebarOpen}
              active={isActive('/admin/classes')}
              label={t('Classes')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
            />
            <SidebarLink
              to="/admin/teachers"
              open={sidebarOpen}
              active={isActive('/admin/teachers')}
              label={t('Teachers')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                </svg>
              }
            />
            {enabled('attendance') && <SidebarLink
              to="/admin/attendance"
              open={sidebarOpen}
              active={isActive('/admin/attendance')}
              label={t('Attendance')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              }
            />}
            {enabled('payments') && <SidebarLink
              to="/admin/payments"
              open={sidebarOpen}
              active={isActive('/admin/payments')}
              label={t('Payments')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m12 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2z" />
                </svg>
              }
            />}
            {enabled('teacher_blogs') && <SidebarLink
              to="/admin/teacher-blogs"
              open={sidebarOpen}
              active={isActive('/admin/teacher-blogs')}
              label={t('Teacher Blogs')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              }
            />}
            <SidebarLink
              to="/admin/enrollments"
              open={sidebarOpen}
              active={isActive('/admin/enrollments')}
              label={t('Enrollments')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
            />
            {enabled('instagram') && <SidebarLink
              to="/admin/instagram-campaigns"
              open={sidebarOpen}
              active={isActive('/admin/instagram-campaigns')}
              label={t('Instagram Campaigns')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 3h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7a4 4 0 014-4zm5 5a4 4 0 100 8 4 4 0 000-8zm4.5-.5h.01" />
                </svg>
              }
            />}
            {enabled('payments') && <SidebarLink
              to="/admin/payment-accounts"
              open={sidebarOpen}
              active={isActive('/admin/payment-accounts')}
              label={t('Payment Accounts')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h16a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            />}
            {enabled('featured_content') && <SidebarLink
              to="/admin/sponsorships"
              open={sidebarOpen}
              active={isActive('/admin/sponsorships')}
              label={t('Sponsorships')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.368 2.447a1 1 0 00-.363 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.367-2.447a1 1 0 00-1.176 0l-3.367 2.447c-.784.57-1.838-.197-1.539-1.118l1.286-3.957a1 1 0 00-.363-1.118L2.83 9.384c-.783-.57-.38-1.81.588-1.81h4.163a1 1 0 00.95-.69l1.286-3.957z" />
                </svg>
              }
            />}
            <SidebarLink
              to="/admin/teacher-approvals"
              open={sidebarOpen}
              active={isActive('/admin/teacher-approvals')}
              label={t('Teacher Approvals')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            {enabled('parent_portal') && <SidebarLink
              to="/admin/parent-links"
              open={sidebarOpen}
              active={isActive('/admin/parent-links')}
              label={t('Parent Links')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-4-4h-1m-4 6H2v-2a4 4 0 014-4h3m7-7a3 3 0 11-6 0 3 3 0 016 0zM8 7a3 3 0 11-6 0 3 3 0 016 0" />
                </svg>
              }
            />}
            {enabled('ratings') && <SidebarLink
              to="/admin/moderation"
              open={sidebarOpen}
              active={isActive('/admin/moderation')}
              label={t('Moderation')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              }
            />}
            {enabled('payments') && <SidebarLink
              to="/admin/subscriptions"
              open={sidebarOpen}
              active={isActive('/admin/subscriptions')}
              label={t('Subscriptions')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m12 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2z" />
                </svg>
              }
            />}
            <SidebarLink
              to="/admin/grades"
              open={sidebarOpen}
              active={isActive('/admin/grades')}
              label={t('Grade Levels')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />
            {enabled('audit_logs') && <SidebarLink
              to="/admin/audit-log"
              open={sidebarOpen}
              active={isActive('/admin/audit-log')}
              label={t('Audit Log')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h1m-1 4h1" />
                </svg>
              }
            />}
            {enabled('activity_logs') && <SidebarLink
              to="/admin/connections"
              open={sidebarOpen}
              active={isActive('/admin/connections')}
              label={t('Connections & Logs')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
            />}
            {enabled('monitoring') && <SidebarLink
              to="/admin/monitoring"
              open={sidebarOpen}
              active={isActive('/admin/monitoring')}
              label={t('System Monitoring')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h4l2-7 4 14 2-7h6" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 21h16a1 1 0 001-1V4a1 1 0 00-1-1H4a1 1 0 00-1 1v16a1 1 0 001 1z" />
                </svg>
              }
            />}
            {enabled('announcements') && <SidebarLink
              to="/admin/announcements"
              open={sidebarOpen}
              active={isActive('/admin/announcements')}
              label={t('Announcements')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              }
            />}
            <SidebarLink
              to="/admin/school-settings"
              open={sidebarOpen}
              active={isActive('/admin/school-settings')}
              label={t('School Settings')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
            />
            {enabled('contact_messages') && <SidebarLink
              to="/admin/contact-messages"
              open={sidebarOpen}
              active={isActive('/admin/contact-messages')}
              label={t('Contact messages')}
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-16 9h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              }
            />}
          </>
        )}
      </nav>

      {Object.keys(socialLinks || {}).length > 0 && (
        <div className="border-t border-white/10 px-3 py-2">
          <SocialLinks links={socialLinks} maxItems={sidebarOpen ? 9 : 1} className="justify-center" />
        </div>
      )}

      <div className="relative p-3 border-t border-white/10">
        {sidebarOpen ? (
          <Link to="/profile" className="flex items-center gap-3 p-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition">
            <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 ring-2 ring-accent-400/60">
              {user?.profile_picture ? (
                <img src={user.profile_picture} alt="avatar" className="w-9 h-9 object-cover rounded-full" />
              ) : (
                <div className="w-9 h-9 bg-accent-500 rounded-full flex items-center justify-center text-white font-bold text-sm">{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-primary-300 truncate capitalize">{user?.roles?.[0]?.name}</p>
            </div>
          </Link>
        ) : (
          <Link to="/profile" className="w-9 h-9 block mx-auto">
            {user?.profile_picture ? (
              <img src={user.profile_picture} alt="avatar" className="w-9 h-9 object-cover rounded-full ring-2 ring-accent-400/60" />
            ) : (
              <div className="w-9 h-9 bg-accent-500 rounded-full flex items-center justify-center text-white font-bold text-sm mx-auto ring-2 ring-accent-400/60">{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
            )}
          </Link>
        )}
      </div>
      </aside>
    </>
  );
}

export default Sidebar;
